import {toRaw} from 'vue';
import cloneDeep from 'lodash.clonedeep';
import {resolveSurfaceEditorTemplateText} from '~/composables/surface-editor/template-dynamic-text.js';

export function useSurfaceEditorStoreProject({
  conf,
  project,
  surfaces,
  selectElement,
  editElement,
  clearSelection,
  setActiveSurface,
  pauseUndo,
  resumeUndo,
  commitUndo,
  clearUndoHistory,
  cancelActiveInteraction
}) {
  const surfaceEditorUtils = useSurfaceEditorUtils();
  const api = useSurfaceEditorApi();
  const isLoading = ref(false);
  const clipboard = ref(null);

  const isBackgroundElement = (element) => element?.background;

  function findElementContext({element}) {
    if (!element?.id) return null;

    return surfaceEditorUtils.findElementInSurface({
      elementId: element.id,
      surfaces: surfaces.value
    });
  }

  /**
   * Updates the z property of all elements in a surface to reflect their position in the array.
   * Lower index = lower z-index (behind), higher index = higher z-index (in front).
   */
  function updateElementZIndices({surface}) {
    surface.elements.forEach((element, index) => {
      element.z = index;
    });
  }

  function getTemplateToSurfaceScale({template, surface}) {
    const templateWidth = Number(template?.width);
    const templateHeight = Number(template?.height);
    const surfaceWidth = Number(surface?.width);
    const surfaceHeight = Number(surface?.height);

    if (!templateWidth || !templateHeight || !surfaceWidth || !surfaceHeight) {
      return {
        xScale: 1,
        yScale: 1
      };
    }

    return {
      xScale: surfaceWidth / templateWidth,
      yScale: surfaceHeight / templateHeight
    };
  }

  function getProportionalScale({xScale, yScale}) {
    return Math.min(xScale, yScale);
  }

  function scaleTemplateElementToSurface({element, xScale, yScale}) {
    const adjustedElement = Object.assign(cloneDeep(element), {
      id: crypto.randomUUID()
    });
    adjustedElement.content = adjustedElement.content || {};

    const isFreeformResizeElement = SURFACE_EDITOR_FREEFORM_RESIZE_ELEMENT_TYPES.includes(adjustedElement.type);
    const proportionalScale = getProportionalScale({xScale, yScale});
    const widthScale = isFreeformResizeElement ? xScale : proportionalScale;
    const heightScale = isFreeformResizeElement ? yScale : proportionalScale;

    adjustedElement.x = Math.round((adjustedElement.x || 0) * xScale);
    adjustedElement.y = Math.round((adjustedElement.y || 0) * yScale);
    adjustedElement.width = Math.max(
      SURFACE_EDITOR_MIN_ELEMENT_DIMENSION,
      Math.round((adjustedElement.width || 0) * widthScale)
    );
    adjustedElement.height = Math.max(
      SURFACE_EDITOR_MIN_ELEMENT_DIMENSION,
      Math.round((adjustedElement.height || 0) * heightScale)
    );

    switch (adjustedElement.type) {
      case SURFACE_EDITOR_ELEMENT_TYPE.image:
        adjustedElement.content.scale = (adjustedElement.content.scale || 1) * proportionalScale;
        adjustedElement.content.offset_x = Math.round((adjustedElement.content.offset_x || 0) * widthScale);
        adjustedElement.content.offset_y = Math.round((adjustedElement.content.offset_y || 0) * heightScale);
        adjustedElement.content.forever_file_id = null;
        break;
      case SURFACE_EDITOR_ELEMENT_TYPE.text:
        adjustedElement.content.scale = (adjustedElement.content.scale || 1) * proportionalScale;
        break;
      default:
        if (adjustedElement.content?.forever_file_id) {
          adjustedElement.content.forever_file_id = null;
        }
        break;
    }

    return adjustedElement;
  }

  function scaleTemplateBackgroundImageElementToSurface({element, surface, xScale, yScale}) {
    const adjustedElement = Object.assign(cloneDeep(element), {
      id: crypto.randomUUID(),
      x: 0,
      y: 0,
      width: surface.width,
      height: surface.height
    });
    adjustedElement.content = adjustedElement.content || {};

    const imageWidth = adjustedElement.content.image_width;
    const imageHeight = adjustedElement.content.image_height;
    const currentScale = adjustedElement.content.scale || 1;
    const offsetX = adjustedElement.content.offset_x || 0;
    const offsetY = adjustedElement.content.offset_y || 0;
    const proportionalScale = getProportionalScale({xScale, yScale});

    if (imageWidth && imageHeight) {
      const initialCoveredWidth = offsetX + (imageWidth * currentScale);
      const initialCoveredHeight = offsetY + (imageHeight * currentScale);
      const minScaleRatioX = initialCoveredWidth > 0
        ? adjustedElement.width / initialCoveredWidth
        : proportionalScale;
      const minScaleRatioY = initialCoveredHeight > 0
        ? adjustedElement.height / initialCoveredHeight
        : proportionalScale;
      const finalScaleRatio = Math.max(
        proportionalScale,
        minScaleRatioX,
        minScaleRatioY
      );

      adjustedElement.content.scale = currentScale * finalScaleRatio;
      adjustedElement.content.offset_x = Math.round(offsetX * finalScaleRatio);
      adjustedElement.content.offset_y = Math.round(offsetY * finalScaleRatio);
    } else {
      adjustedElement.content.scale = currentScale;
    }

    adjustedElement.content.forever_file_id = null;

    return adjustedElement;
  }

  function isFullSurfaceTemplateImage({element, template}) {
    return element?.type === SURFACE_EDITOR_ELEMENT_TYPE.image
      && Number(element?.x || 0) === 0
      && Number(element?.y || 0) === 0
      && Number(element?.width) === Number(template?.width)
      && Number(element?.height) === Number(template?.height);
  }

  async function setProject({project: p}) {
    clearSelection();

    project.value = p;
    surfaces.value = p.surfaces;

    await nextTick();

    clearUndoHistory();
  }

  async function createProject() {
    isLoading.value = true;

    const projectData = await api.createProject({
      width: conf.value.width,
      height: conf.value.height,
      projectType: conf.value.projectType
    });

    setProject({project: projectData});

    isLoading.value = false;

    return projectData;
  }

  async function getProject({id}) {
    isLoading.value = true;

    const projectData = await api.getProject({projectId: id});

    setProject({project: projectData});

    isLoading.value = false;

    return projectData;
  }

  function addElement({element, surface, index, commit = true}) {
    const newElement = Object.assign({}, element, {id: crypto.randomUUID()});

    pauseUndo();

    if (typeof index === 'number') {
      const safeIndex = Math.min(Math.max(index, 0), surface.elements.length);
      surface.elements.splice(safeIndex, 0, newElement);
    } else {
      surface.elements.push(newElement);
    }

    // Update z property for all elements to reflect their array position
    updateElementZIndices({surface});

    resumeUndo();

    if (commit) {
      commitUndo();
    }

    selectElement({element: newElement});

    return newElement;
  }

  function deleteElement({element, surface, commit = true}) {
    if (isBackgroundElement(element)) return;

    const context = surface ? {surface} : findElementContext({element});
    const targetSurface = context?.surface;
    if (!targetSurface) return;

    const index = targetSurface.elements.findIndex((e) => e.id === element.id);

    if (index > -1) {
      cancelActiveInteraction?.({surface: targetSurface});
      pauseUndo();

      targetSurface.elements.splice(index, 1);

      // Update z property for remaining elements
      updateElementZIndices({surface: targetSurface});

      resumeUndo();

      selectElement({element: null});
      editElement({element: null});
      if (commit) {
        commitUndo();
      }
    }
  }

  function updateElement({element, updates, commit = true}) {
    Object.assign(element, {
      ...updates,
      content: Object.assign(element.content, {
        ...updates.content,
        fill: updates.content?.fill || element.content.fill
      })
    });

    if (commit) {
      commitUndo();
    }
  }

  function resetTextToScale({element}) {
    pauseUndo();

    element.content.font_size = element.content.font_size * element.content.scale;
    // element.content.lineHeight = element.content.lineHeight * element.content.scale;
    element.content.scale = 1;

    resumeUndo();
  }

  function reorderElements({element, action}) {
    if (isBackgroundElement(element)) return;

    const {elementIndex, surface} = surfaceEditorUtils.findElementInSurface({
      elementId: element.id,
      surfaces: surfaces.value
    });
    const overlaps = surfaceEditorUtils.getOverlappingElements({element, surface});
    const elementOverlapIndex = overlaps.findIndex(overlap => overlap.element.id === element.id);

    let newIndex = (() => {
      let targetOverlap;

      switch (action) {
        case SURFACE_EDITOR_REORDER_ACTIONS.forward:
          targetOverlap = overlaps[Math.min(elementOverlapIndex + 1, overlaps.length - 1)];

          return targetOverlap.index;
        case SURFACE_EDITOR_REORDER_ACTIONS.backward:
          targetOverlap = overlaps[Math.max(elementOverlapIndex - 1, 0)];

          return targetOverlap.index;
        case SURFACE_EDITOR_REORDER_ACTIONS.front:
          return surface.elements.length - 1;
        case SURFACE_EDITOR_REORDER_ACTIONS.back:
          return 0;
      }
    })();

    newIndex = Math.min(Math.max(newIndex, 0), surface.elements.length - 1);

    if (newIndex === elementIndex) {
      return;
    }

    pauseUndo();

    //note: need to make a copy of elements so we don't trigger reactivity until both actions are done
    const elementsCopy = surface.elements.slice();
    elementsCopy.splice(elementIndex, 1);
    elementsCopy.splice(newIndex, 0, element);
    surface.elements = elementsCopy;

    // Update z property for all elements after reordering
    updateElementZIndices({surface});

    resumeUndo();
    commitUndo();
  }

  async function applyTemplate({template, surface}) {
    const resolvedTemplate = resolveSurfaceEditorTemplateText({
      surface: template,
      data: conf.value?.dynamicTextData
    });
    const {xScale, yScale} = getTemplateToSurfaceScale({
      template: resolvedTemplate,
      surface
    });

    pauseUndo();

    const templateElements = resolvedTemplate.elements || [];
    const backgroundImageElement = templateElements.find(element => (
      isFullSurfaceTemplateImage({
        element,
        template: resolvedTemplate
      })
    ));
    const foregroundTemplateElements = backgroundImageElement
      ? templateElements.filter(element => element !== backgroundImageElement)
      : templateElements;

    surface.background = Object.assign({}, cloneDeep(resolvedTemplate.background), {
      id: surface.background.id,
      width: surface.width,
      height: surface.height
    });

    const scaledForegroundElements = foregroundTemplateElements.map(element => (
      scaleTemplateElementToSurface({
        element,
        xScale,
        yScale
      })
    ));

    // Temporary bridge until the API supports image-type backgrounds directly:
    // if a template contains a full-surface image element, keep it as a normal
    // image element, but size it like a background and place it at the bottom.
    surface.elements = backgroundImageElement
      ? [
          scaleTemplateBackgroundImageElementToSurface({
            element: backgroundImageElement,
            surface,
            xScale,
            yScale
          }),
          ...scaledForegroundElements
        ]
      : scaledForegroundElements;

    // Update z property for all template elements
    updateElementZIndices({surface});

    resumeUndo();
    commitUndo();
  }

  function resizeSurface({surface, width, height}) {
    if (!surface || !width || !height) return;

    const newSurface = {
      id: crypto.randomUUID(),
      width,
      height
    };

    const xScale = width / surface.width;
    const yScale = height / surface.height;

    pauseUndo();

    const portElement = element => {
      const cloned = cloneDeep(element);
      cloned.content = cloned.content || {};

      const isFreeform = SURFACE_EDITOR_FREEFORM_RESIZE_ELEMENT_TYPES.includes(cloned.type);
      const proportionalScale = getProportionalScale({xScale, yScale});
      const wScale = isFreeform ? xScale : proportionalScale;
      const hScale = isFreeform ? yScale : proportionalScale;

      cloned.x = Math.round((cloned.x || 0) * xScale);
      cloned.y = Math.round((cloned.y || 0) * yScale);
      cloned.width = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round((cloned.width || 0) * wScale));
      cloned.height = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round((cloned.height || 0) * hScale));

      switch (cloned.type) {
        case SURFACE_EDITOR_ELEMENT_TYPE.image:
          cloned.content.scale = (cloned.content.scale || 1) * proportionalScale;
          cloned.content.offset_x = Math.round((cloned.content.offset_x || 0) * wScale);
          cloned.content.offset_y = Math.round((cloned.content.offset_y || 0) * hScale);
          break;
        case SURFACE_EDITOR_ELEMENT_TYPE.text:
          cloned.content.scale = (cloned.content.scale || 1) * proportionalScale;
          break;
      }

      return cloned;
    };

    newSurface.elements = surface.elements.map(portElement);
    newSurface.background = portElement(surface.background);
    newSurface.background.id = crypto.randomUUID();
    newSurface.background.width = width;
    newSurface.background.height = height;

    updateElementZIndices({surface: newSurface});

    const oldSurfaceIndex = surfaces.value.indexOf(s => s.id === surface.id);

    surfaces.value.splice(oldSurfaceIndex, 1, newSurface);

    setActiveSurface({surface: newSurface});

    resumeUndo();
    commitUndo();
  }

  function resetSurface({surface, backgroundColor = '#FFFFFF'}) {
    if (!surface) return;

    pauseUndo();
    surface.elements = [];

    surface.background.content = {
      fill: {
        type: SURFACE_EDITOR_FILL_TYPES.solid,
        color: backgroundColor
      }
    };

    resumeUndo();
    commitUndo();
  }

  function copyElement({element}) {
    if (isBackgroundElement(element)) return;

    const context = findElementContext({element});

    if (!context?.surface) return;

    clipboard.value = {
      element: cloneDeep(toRaw(context.element)),
      surfaceId: context.surface.id
    };
  }

  function offsetElement(element) {
    const copy = cloneDeep(toRaw(element));
    copy.x += 50;
    copy.y += 50;

    return copy;
  }

  function pasteElement() {
    if (!clipboard.value) return;

    const surface = surfaces.value.find(s => s.id === clipboard.value.surfaceId);

    if (!surface) return;

    const sourceIndex = surface.elements.findIndex(e => e.id === clipboard.value.element.id);
    const insertIndex = sourceIndex > -1 ? sourceIndex + 1 : surface.elements.length;

    return addElement({
      element: offsetElement(clipboard.value.element),
      surface,
      index: insertIndex
    });
  }

  function duplicateElement({element}) {
    if (isBackgroundElement(element)) return;

    copyElement({element});

    return pasteElement();
  }

  function nudgeElement({element, dx = 0, dy = 0}) {
    if (isBackgroundElement(element)) return;

    const context = findElementContext({element});
    if (!context?.element) return;

    updateElement({
      element: context.element,
      updates: {
        x: Math.max(0, context.element.x + dx),
        y: Math.max(0, context.element.y + dy)
      }
    });
  }

  function resizeElementProportionally({element, delta = 0}) {
    if (isBackgroundElement(element) || !delta) return;

    const context = findElementContext({element});
    const target = context?.element;

    if (!target?.width || !target?.height) return;
    if (target.type === SURFACE_EDITOR_ELEMENT_TYPE.shape) {
      return resizeShapeElement({element: target, delta});
    }

    const aspect = target.width / target.height;
    const nextHeight = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, target.height + delta);
    const nextWidth = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(nextHeight * aspect));

    const widthScale = nextWidth / target.width;
    const heightScale = nextHeight / target.height;

    const contentUpdates = (() => {
      switch (target.type) {
        case SURFACE_EDITOR_ELEMENT_TYPE.image:
          return {
            scale: (target.content?.scale || 1) * widthScale,
            offset_x: (target.content?.offset_x || 0) * widthScale,
            offset_y: (target.content?.offset_y || 0) * heightScale
          };
        case SURFACE_EDITOR_ELEMENT_TYPE.text:
          return {
            scale: (target.content?.scale || 1) * widthScale,
            font_size: Math.max(
              SURFACE_EDITOR_MIN_ELEMENT_DIMENSION,
              Math.round((target.content?.font_size || 0) * heightScale)
            )
          };
        case SURFACE_EDITOR_ELEMENT_TYPE.shape:
          return {
            scale: (target.content?.scale || 1) * widthScale
          };
        default:
          return undefined;
      }
    })();

    updateElement({
      element: target,
      updates: {
        width: nextWidth,
        height: nextHeight,
        ...(contentUpdates && {content: contentUpdates})
      }
    });
  }

  function resizeShapeElement({element, delta = 0}) {
    if (isBackgroundElement(element) || !delta) return;

    const context = findElementContext({element});
    const target = context?.element;

    if (!target?.width || !target?.height) return;
    if (target.type !== SURFACE_EDITOR_ELEMENT_TYPE.shape) return;

    const nextWidth = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, target.width + delta);
    const nextHeight = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, target.height + delta);

    updateElement({
      element: target,
      updates: {
        width: nextWidth,
        height: nextHeight
      }
    });
  }

  function snapImageToBackground({element}) {
    const context = findElementContext({element});
    const targetElement = context?.element;
    const surface = context?.surface;

    if (!targetElement || !surface) return;
    if (targetElement.type !== SURFACE_EDITOR_ELEMENT_TYPE.image) return;

    const imageWidth = targetElement.content?.image_width;
    const imageHeight = targetElement.content?.image_height;
    if (!imageWidth || !imageHeight) return;

    const surfaceWidth = surface.width;
    const surfaceHeight = surface.height;
    if (!surfaceWidth || !surfaceHeight) {
      console.warn('Surface dimensions missing for snapImageToBackground');
      return;
    }

    const scale = Math.max(surfaceWidth / imageWidth, surfaceHeight / imageHeight);
    const newWidth = surfaceWidth;
    const newHeight = surfaceHeight;
    const scaledImageWidth = imageWidth * scale;
    const scaledImageHeight = imageHeight * scale;
    const x = 0;
    const y = 0;
    const offsetX = (newWidth - scaledImageWidth) / 2;
    const offsetY = (newHeight - scaledImageHeight) / 2;

    updateElement({
      element: targetElement,
      updates: {
        x,
        y,
        width: newWidth,
        height: newHeight,
        rotation: 0,
        content: {
          scale,
          offset_x: offsetX,
          offset_y: offsetY,
          image_rotation: 0
        }
      }
    });
  }

  return {
    surfaces,
    isLoadingProject: isLoading,

    createProject,
    getProject,

    addElement,
    deleteElement,
    updateElement,
    resetTextToScale,
    reorderElements,
    clipboard,
    copyElement,
    pasteElement,
    duplicateElement,
    nudgeElement,
    resizeElementProportionally,
    resizeShapeElement,
    snapImageToBackground,

    applyTemplate,
    resetSurface,
    resizeSurface
  };
}
